import React, { FC, useState } from 'react';
import { Product } from 'shared/types/product/Product';
import { hasHtmlContent, sanitize } from 'helpers/utils/htmlSanitize';
import Typography from 'components/commercetools-ui/atoms/typography';
import ProductsLoadError from 'components/commercetools-ui/atoms/products-load-error';
import ProductTile, { ProductTileLayout } from 'components/commercetools-ui/organisms/product/product-tile';

import { SectionWithProductGridContent } from './types';
import { useAdditionalCss } from '../helpers/useAdditionalCss';

/** Tiles shown before the visitor asks for the rest. */
const INITIAL_PRODUCTS_DISPLAYED = 4;

interface ProductGridProps {
  content: SectionWithProductGridContent;
  /** Already in display order (see useProductBlock). */
  products: Product[];
  tileLayout: ProductTileLayout;
}

/**
 * Two-column grid of product tiles — the first four, then a "Show all products" button for the rest —
 * constrained to the long-form article measure (47rem) so it sits in the same column as `html-only-blog`
 * copy (it replaces the legacy renovation-guide `commercetools_product` block). Presentational only, so
 * the tiles land in the SSR HTML.
 */
const ProductGrid: FC<ProductGridProps> = ({ content, products, tileLayout }) => {
  const [showAll, setShowAll] = useState(false);

  const title = content?.title;
  const description = content?.description;
  const hasDescription = hasHtmlContent(description);
  const additional_css = content?.additional_css;
  const additional_css_class_names = content?.additional_css_class_names;
  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'ProductGrid');

  const visibleProducts = showAll ? products : products.slice(0, INITIAL_PRODUCTS_DISPLAYED);

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      <div
        className={`px-24 md:px-28 lg:px-48 xl:px-60 2xl:px-80 pt-24 pb-24 lg:pt-40 lg:pb-40 2xl:pt-60 2xl:pb-60 ${[additional_css_class_names, uniqueClass].filter(Boolean).join(' ')}`}
      >
        <div className="m-auto w-[min(100%,47rem)]">
          {title && (
            <Typography
              className={`${hasDescription ? 'pb-12' : 'pb-24'} text-24 font-medium leading-10 text-primary-black`}
              as="h3"
            >
              {title}
            </Typography>
          )}
          {/* Article-column body copy — the scale, paragraph rhythm, list markers and blue blog link
              colour of the `html-only-blog` text this grid sits between, so a lead-in (or a legacy
              product link list moved out of the body) reads as part of the article. Those global
              classes cannot simply be reused: they also carry the wrapper's own padding and measure. */}
          {hasDescription && (
            <div
              className="pb-24 text-14 font-normal text-primary-black md:text-16 2xl:text-18 [&>*:first-child]:!mt-0 [&>*:last-child]:!mb-0 [&_a]:!text-[#337AB7] hover:[&_a]:!no-underline [&_ol]:ml-20 [&_ol]:list-decimal [&_ol>li]:my-10 [&_p]:my-10 [&_ul]:ml-20 [&_ul]:list-disc [&_ul>li]:my-10"
              dangerouslySetInnerHTML={{ __html: sanitize(description) }}
            />
          )}
          {/* No products (nothing configured to query, or the preload failed) — the message replaces the
              grid but stays INSIDE the section, so the heading above it still names the broken block. */}
          {products.length === 0 ? (
            <ProductsLoadError className="py-24 lg:py-40" />
          ) : (
            <>
              <div className="grid grid-cols-2 gap-8">
                {visibleProducts.map((product, index) => (
                  <ProductTile key={product.productId || index} product={product} layout={tileLayout} />
                ))}
              </div>
              {!showAll && products.length > INITIAL_PRODUCTS_DISPLAYED && (
                <div className="mt-24 text-center">
                  <button className="bg-[#2B2C39] px-25 py-13 text-white" onClick={() => setShowAll(true)}>
                    Show all products
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ProductGrid;
